import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { LmsAnalyticsData } from './lms-analytics.data';
import ApiService from '../../_services/ApiService';
import Config from '../../_config/config';

interface AiChatProps {
  data: LmsAnalyticsData;
  showDataSource?: boolean;
  expandedWidgets: Set<string>;
}

export const AiChat: React.FC<AiChatProps> = ({ data, showDataSource = false, expandedWidgets }) => {
  const [chatMessages, setChatMessages] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatInput('');

    // Add user message to chat
    setChatMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    setChatLoading(true);

    try {
      // Check if user is logged in
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser || currentUser === 'undefined') {
        throw new Error('Authentication required. Please login to use AI chat features.');
      }

      // Prepare SUMMARIZED context to avoid token limits
      const contextPayload = {
        metrics: {
          totalLearners: data.learners,
          overdueCount: data.overdue,
          atRiskCount: data.risks.length,
          programCount: data.programs,
          totalEnrollments: data.rawRecords?.allEnrollments?.length || 0,
          totalLessonPlans: data.rawRecords?.lessonPlans?.length || 0
        },
        competencies: data.competencies?.slice(0, 10) || [], // Limit to 10
        risksSample: data.risks?.slice(0, 10).map(r => ({
          learner: r.learner,
          course: r.course,
          progress: r.progress,
          expected: r.expected,
          riskCategory: r.riskCategory,
          riskScore: r.riskScore
        })) || [],
        // Sample of enrollments instead of all
        enrollmentSample: data.rawRecords?.allEnrollments?.slice(0, 5).map(e => ({
          learnerName: e.learnerName,
          courseName: e.courseName,
          status: e.status,
          progress: e.progress
        })) || []
      };

      // Build conversation history for context
      const conversationHistory = chatMessages.map(msg =>
        `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`
      ).join('\n\n');

      const prompt = `You are an AI assistant analyzing learning management system (LMS) data.

Context (LMS Data):
${JSON.stringify(contextPayload, null, 2)}

Previous Conversation:
${conversationHistory || 'No previous conversation'}

Current User Question: ${userMessage}

Instructions:
- Remember the previous conversation context when answering
- If the user refers to "he", "she", "they", or "this learner/course", look at the previous messages to understand who/what they're referring to
- Provide a clear, concise answer based on both the data and conversation history
- Keep your response under 150 words unless providing detailed analysis is necessary`;

      const response = await ApiService.generateLearningAnalytics({ prompt });

      console.log('AI Chat Response:', response);

      let responseData = response?.data?.data || response?.data?.response || response?.data;

      // Check if response is valid
      if (!responseData || responseData === 'undefined' || responseData === undefined) {
        throw new Error(`Unable to connect to AI service at ${Config.nodeSiteUrl}. Please try again later.`);
      }

      // Clean markdown formatting
      if (typeof responseData === 'string') {
        responseData = responseData
          .replace(/```json\s*/g, '')
          .replace(/```\s*/g, '')
          .trim();
      }

      // Add AI response to chat
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: responseData,
        timestamp: new Date()
      }]);

    } catch (error: any) {
      console.error('Chat error:', error);
      const errorMessage = error?.message || 'Sorry, I encountered an error. Please try again.';

      // Special handling for authentication errors
      let fullMessage = `❌ ${errorMessage}`;
      if (errorMessage.includes('Authentication required') || errorMessage.includes('login')) {
        fullMessage += '\n\n👉 Please [login](/login) to continue using AI features.';
      } else {
        fullMessage += '\n\nIf the problem persists, please contact support.';
      }

      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: fullMessage,
        timestamp: new Date()
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: expandedWidgets.has('ai-chat') ? 'calc(100vh - 200px)' : '400px',
      maxHeight: expandedWidgets.has('ai-chat') ? 'none' : '400px'
    }}>
      {/* Messages Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        background: 'var(--surface-page)',
        borderRadius: '8px',
        marginBottom: '16px',
        minHeight: '250px'
      }}>
        {chatMessages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Start a conversation</div>
            <div style={{ fontSize: '12px', lineHeight: '1.6', marginBottom: '16px' }}>
              Ask me anything about your LMS data:
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-faint)', textAlign: 'left', maxWidth: '400px', margin: '0 auto' }}>
              <div style={{ marginBottom: '6px' }}>• "Which learners need immediate attention?"</div>
              <div style={{ marginBottom: '6px' }}>• "Why is {data.competencies[0] || 'this competency'} at risk?"</div>
              <div style={{ marginBottom: '6px' }}>• "What trends do you see in enrollments?"</div>
              <div>• "How can I improve completion rates?"</div>
            </div>
          </div>
        ) : (
          <>
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '12px'
                }}
              >
                <div style={{
                  maxWidth: '80%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  background: msg.role === 'user'
                    ? 'var(--gradient-brand)'
                    : 'white',
                  color: msg.role === 'user' ? 'white' : '#334155',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  {msg.role === 'assistant' && (
                    <div style={{ fontSize: '16px', marginBottom: '8px' }}>🤖</div>
                  )}
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown
                      components={{
                        p: ({node, ...props}) => <p style={{ margin: '8px 0' }} {...props} />,
                        strong: ({node, ...props}) => <strong style={{ fontWeight: 700, color: 'var(--text-strong)' }} {...props} />,
                        ul: ({node, ...props}) => <ul style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }} {...props} />,
                        ol: ({node, ...props}) => <ol style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }} {...props} />,
                        li: ({node, ...props}) => <li style={{ marginBottom: '4px' }} {...props} />,
                        code: ({node, className, children, ...props}: any) => {
                          const isInline = !className;
                          return isInline ? (
                            <code style={{ background: 'var(--surface-sunken)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', fontFamily: 'monospace' }} {...props}>{children}</code>
                          ) : (
                            <code style={{ display: 'block', background: 'var(--surface-sunken)', padding: '12px', borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace', overflowX: 'auto', marginTop: '8px', marginBottom: '8px' }} {...props}>{children}</code>
                          );
                        }
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1.5s ease-in-out 0.2s infinite' }}></div>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1.5s ease-in-out 0.4s infinite' }}></div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Input Area */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
          placeholder="Ask a question about your LMS data..."
          disabled={chatLoading}
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid var(--border-default)',
            fontSize: '13px',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
          onBlur={(e) => e.target.style.borderColor = 'var(--border-default)'}
        />
        <button
          onClick={sendChatMessage}
          disabled={chatLoading || !chatInput.trim()}
          style={{
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            background: chatLoading || !chatInput.trim()
              ? '#e2e8f0'
              : 'var(--gradient-brand)',
            color: chatLoading || !chatInput.trim() ? '#94a3b8' : 'white',
            fontSize: '13px',
            fontWeight: 600,
            cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!chatLoading && chatInput.trim()) {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {chatLoading ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
};
